import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  amazonCatalogApi,
  BULK_ACTIONS,
  type AIRecommendation,
  type ActivityEntry,
  type CatalogHealth,
  type DashboardSummary,
  type Product,
  type ProductGridRow,
  type RelationshipGraph,
  type SearchResult,
} from '../../api/amazoncatalog';

const WORKSPACE = (getWorkspaceId() ?? '');

export function CatalogDashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [health, setHealth] = useState<CatalogHealth | null>(null);
  const [rows, setRows] = useState<ProductGridRow[]>([]);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [recs, setRecs] = useState<AIRecommendation[]>([]);
  const [graph, setGraph] = useState<RelationshipGraph | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, h, grid, act] = await Promise.all([
        amazonCatalogApi.dashboard(WORKSPACE),
        amazonCatalogApi.catalogHealth(WORKSPACE),
        amazonCatalogApi.productGrid(WORKSPACE),
        amazonCatalogApi.listActivity(),
      ]);
      setDashboard(dash);
      setHealth(h);
      setRows(grid.rows);
      setTotal(grid.total);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load catalog');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runSearch = async () => {
    if (!searchText.trim()) {
      setSearchResult(null);
      return;
    }
    setBusy(true);
    try {
      const result = await amazonCatalogApi.globalSearch(searchText, WORKSPACE);
      setSearchResult(result);
    } finally {
      setBusy(false);
    }
  };

  const loadProduct = async (id: string) => {
    setSelectedId(id);
    try {
      const [p, recommendations, rels] = await Promise.all([
        amazonCatalogApi.getProduct(id),
        amazonCatalogApi.recommendations(id),
        amazonCatalogApi.relationships(id),
      ]);
      setProduct(p);
      setRecs(recommendations);
      setGraph(rels);
    } catch {
      setProduct(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (action: string) => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      await amazonCatalogApi.triggerBulk({
        action: action === 'recalculate_quality' ? 'recalculate_quality' : action,
        product_ids: [...selectedIds],
        workspace_id: WORKSPACE,
        parameters: action === 'update_status' ? { status: 'active' } : undefined,
      });
      setSelectedIds(new Set());
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const displayRows = searchResult?.products?.length
    ? searchResult.products.map((p) => ({
        id: p.id,
        asin: p.identifiers.asin,
        sku: p.identifiers.sku,
        title: p.content.title,
        brand: p.brand.brand,
        status: p.status,
        quality_score: p.quality.overall_score,
        ai_ready: p.quality.ai_ready,
        updated_at: p.updated_at,
      }))
    : rows.filter((r) => !statusFilter || r.status === statusFilter);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Product Catalog (PIM)</h1>
          <p className="mt-1 text-sm text-slate-600">
            Authoritative product information management — search, enrich, and operate at enterprise scale
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
        >
          Refresh
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
      )}

      {dashboard && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {[
            { label: 'Products', value: dashboard.total_products },
            { label: 'Active', value: dashboard.active_products },
            { label: 'Draft', value: dashboard.draft_products },
            { label: 'AI Ready', value: dashboard.ai_ready_products },
            { label: 'Indexed', value: dashboard.index_health.indexed_products },
            { label: 'Avg Quality', value: `${Math.round(dashboard.avg_quality_score * 100)}%` },
          ].map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase text-slate-400">{c.label}</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {health && (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
          <h2 className="font-semibold text-emerald-900">Catalog Health</h2>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-emerald-800">
            <span>Overall: {Math.round(health.overall_health * 100)}%</span>
            <span>Content: {Math.round(health.content_completeness * 100)}%</span>
            <span>Images: {Math.round(health.image_completeness * 100)}%</span>
            <span>Attributes: {Math.round(health.attribute_completeness * 100)}%</span>
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Global search (title, ASIN, SKU, brand…)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          className="min-w-[240px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <button
          type="button"
          disabled={busy}
          onClick={runSearch}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          Search
        </button>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {BULK_ACTIONS.map((action) => (
          <button
            key={action}
            type="button"
            disabled={busy || selectedIds.size === 0}
            onClick={() => runBulk(action)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium capitalize disabled:opacity-40"
          >
            Bulk {action.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 flex justify-between">
              <h2 className="font-semibold">Enterprise Product Grid</h2>
              <span className="text-sm text-slate-500">{searchResult ? searchResult.total : total} products</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2">ASIN / SKU</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Quality</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayRows.map((row) => (
                    <tr
                      key={row.id}
                      className={`cursor-pointer hover:bg-slate-50 ${selectedId === row.id ? 'bg-brand-50' : ''}`}
                      onClick={() => loadProduct(row.id)}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)} />
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium text-slate-900">{row.title ?? '—'}</p>
                        <p className="text-xs text-slate-500">{row.brand}</p>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{row.asin ?? row.sku ?? '—'}</td>
                      <td className="px-3 py-2 capitalize">{row.status}</td>
                      <td className="px-3 py-2">
                        {Math.round(row.quality_score * 100)}%
                        {row.ai_ready && <span className="ml-1 text-xs text-violet-600">AI</span>}
                      </td>
                    </tr>
                  ))}
                  {displayRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                        No products. Create products via API or import.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {searchResult?.facets && (
              <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                Search took {searchResult.took_ms}ms · Facets: {JSON.stringify(searchResult.facets.status ?? {})}
              </div>
            )}
          </div>
        </section>

        <section className="space-y-4">
          {product ? (
            <ProductDetailPanel product={product} recs={recs} graph={graph} onPublish={refresh} />
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              Select a product to view details, AI recommendations, and relationships.
            </div>
          )}

          <ActivityPanel entries={activity} />
        </section>
      </div>
    </div>
  );
}

function ProductDetailPanel({
  product,
  recs,
  graph,
  onPublish,
}: {
  product: Product;
  recs: AIRecommendation[];
  graph: RelationshipGraph | null;
  onPublish: () => void;
}) {
  const [tab, setTab] = useState<'details' | 'ai' | 'relations'>('details');

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3">
        <h3 className="font-semibold text-slate-900">{product.content.title ?? 'Untitled'}</h3>
        <p className="text-xs text-slate-500 capitalize">{product.status} · Quality {Math.round(product.quality.overall_score * 100)}%</p>
        {product.status === 'draft' && (
          <button
            type="button"
            onClick={() => amazonCatalogApi.publishProduct(product.id).then(onPublish)}
            className="mt-2 rounded bg-emerald-600 px-3 py-1 text-xs font-medium text-white"
          >
            Publish
          </button>
        )}
      </div>
      <div className="flex gap-1 border-b border-slate-100 px-4">
        {(['details', 'ai', 'relations'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-xs font-medium capitalize ${tab === t ? 'border-b-2 border-brand-500 text-brand-700' : 'text-slate-500'}`}
          >
            {t === 'ai' ? 'AI Panel' : t}
          </button>
        ))}
      </div>
      <div className="p-4 text-sm space-y-3">
        {tab === 'details' && (
          <>
            <p>{product.content.description ?? 'No description.'}</p>
            {product.media && product.media.length > 0 && (
              <p className="text-xs text-slate-500">{product.media.length} image(s)</p>
            )}
            {product.attributes && product.attributes.length > 0 && (
              <ul className="text-xs space-y-1">
                {product.attributes.map((a) => (
                  <li key={a.key}>
                    <span className="font-medium">{a.key}:</span> {String(a.value)}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {tab === 'ai' && (
          <ul className="space-y-2">
            {recs.length === 0 && <li className="text-slate-500">No recommendations.</li>}
            {recs.map((r) => (
              <li key={r.id} className="rounded border border-violet-100 bg-violet-50 px-3 py-2">
                <p className="font-medium text-violet-900">{r.title}</p>
                <p className="text-xs text-violet-700">{r.description}</p>
                <p className="text-xs text-violet-500">Impact {Math.round(r.impact * 100)}% · Confidence {Math.round(r.confidence * 100)}%</p>
              </li>
            ))}
          </ul>
        )}
        {tab === 'relations' && graph && (
          <ul className="space-y-1 text-xs">
            {graph.nodes.map((n) => (
              <li key={n.product_id}>
                {n.title ?? n.product_id} {n.asin && `(${n.asin})`}
              </li>
            ))}
            {graph.relationships.map((r, i) => (
              <li key={i} className="text-slate-500">
                {r.type}: {r.source_id.slice(0, 8)} → {r.target_id.slice(0, 8)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ActivityPanel({ entries }: { entries: ActivityEntry[] }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="font-semibold text-slate-900">Activity Timeline</h3>
      <ul className="mt-3 space-y-2 text-xs">
        {entries.length === 0 && <li className="text-slate-500">No activity yet.</li>}
        {entries.slice(0, 8).map((e) => (
          <li key={e.id} className="border-l-2 border-brand-200 pl-2">
            <p>{e.message}</p>
            <p className="text-slate-400">{new Date(e.created_at).toLocaleString()}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
